import React, { useState } from 'react';
import styled from '@emotion/styled';
import Modal from '../modal/modal';
import themeFlamingo from '../../gatsby-plugin-theme-ui-flamingo';

const StyledModal = styled(Modal)`
  z-index: 10000;
  &&& {
    .dialog {
      overflow-y: auto;
      min-width: 320px;
      max-width: 368px;
      width: calc(100% - 48px);
      padding: 16px 0;

      @media (min-width: 1112px) {
        max-width: 494px;
        width: 100%;
      }
    }

    .dialog-content {
      min-width: 320px;
      width: 100%;
      padding-bottom: 32px;
      @media (min-width: 1112px) {
        padding: 40px 60px 48px;
      }
    }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Title = styled.div`
  font-family: AustinLight, system-ui, sans-serif;
  font-style: normal;
  font-weight: 300;
  font-size: 30px;
  line-height: 119%;
  text-align: center;

  color: ${themeFlamingo.colors.primary};
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.div`
  font-family: GTAmerica, Helvetica, Arial, sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 11px;
  line-height: 148%;

  color: ${themeFlamingo.colors.primary};
`;

const ProductItemsContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const ProductButton = styled.button`
  cursor: pointer;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex-basis: 50%;
  padding: 12px 0 16px;
  background: #ffffff;
  border: 1px solid ${themeFlamingo.colors.primary};
  border-radius: 3px;

  ${(props) =>
    props.selected &&
    `
    border-width: 2px;
    background: #EFEDF3;
  `}

  img {
    height: 70px;
    width: 67px;
  }
`;

const ProductTitle = styled.div`
  font-family: GTAmerica, Helvetica, Arial, sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 160%;

  text-align: center;
  letter-spacing: -0.1px;
  color: ${themeFlamingo.colors.primary};
`;

const PriceContainer = styled.div`
  display: flex;
  justify-content: center;
  font-family: GTAmericaLight, Helvetica, Arial, sans-serif;
  font-style: normal;
  font-weight: 300;
  font-size: 14px;
  line-height: 146%;

  del {
    margin-left: 8px;
    text-decoration: line-through;
    color: ${themeFlamingo.colors.primary};
  }

  span {
    color: ${themeFlamingo.colors.success};
  }
`;

const CadenceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CadenceButton = styled.button`
  cursor: pointer;
  position: relative;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  height: 94px;
  gap: 4px;
  text-align: center;
  border: 1px solid #bfb8cb;
  border-radius: 4px;
  background: #ffffff;
  padding: 1px;
  color: ${themeFlamingo.colors.primary};
  @media (min-width: 768px) {
    height: 80px;
  }

  transition: border-width 0.1s linear;
  ${(props) =>
    props.selected &&
    `
    padding: 0;
    background: #EFEDF3;
    border: 2px solid ${themeFlamingo.colors.primary};
  `}

  > span:nth-of-type(1) {
    font-family: GTAmerica, Helvetica, Arial, sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 140%;
    /* or 22px */
  }

  > span:nth-of-type(2) {
    font-family: GTAmericaLight, Helvetica, Arial, sans-serif;
    font-style: normal;
    font-weight: 300;
    font-size: 14px;
    line-height: 146%;
  }
`;

const ButtonSticker = styled.div`
  border-radius: 3px 0 3px 0;
  position: absolute;
  top: -1px;
  left: 0;
  height: 26px;
  width: 96px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: ${themeFlamingo.colors.primary};
  color: #ffffffff;

  font-family: GTAmerica, Helvetica, Arial, sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 146%;
`;

const CheckIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M7 11.5875L10.0769 15L17 8"
      fill="#2D1754"
    />
    <path d="M7 11.5875L10.0769 15L17 8" stroke="white" strokeWidth="2" />
  </svg>
);

const ButtonCheckedIcon = styled.div`
  color: #ffffffff;
  background: ${themeFlamingo.colors.primary};
  height: 32px;
  width: 32px;
  border-top-right-radius: 2px;
  position: absolute;
  top: -1px;
  right: 0;
`;

const UpdateButton = styled.button`
  cursor: pointer;
  outline: none;
  border: 0;
  background: ${themeFlamingo.colors.primary};
  color: #ffffff;
  width: 100%;
  height: 48px;

  font-family: GTAmerica, Helvetica, Arial, sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 160%;
`;

const cadenceOptions = [
  {
    title: 'Every 2 months',
    subtitle: 'If you shave daily',
    value: 2,
  },

  {
    title: 'Every 4 months',
    subtitle: 'If you shave a few times a week',
    value: 4,
    isMostPopular: true,
  },
  {
    title: 'Every 6 months',
    subtitle: 'If you shave weekly',
    value: 6,
  },
];

const EditRefillModal = (props) => {
  const {
    isOpen,
    onClose,
    refillOptions,
    initialRefillId,
    initialCadence,
    onRefillUpdate,
  } = props;

  const [modalCadence, setModalCadence] = useState(initialCadence);
  const [modalRefillId, setModalRefillId] = useState(initialRefillId);

  const handleUpdate = () => {
    onRefillUpdate(modalRefillId, modalCadence);
    onClose();
  };

  const handleRefillChange = ({ id }) => {
    setModalRefillId(id);
  };

  return (
    <StyledModal isOpen={isOpen} onClose={onClose} closeAriaLabel="Close">
      <Container>
        <Title>Customize Refills</Title>
        <SectionContainer>
          <SectionTitle>PRODUCTS</SectionTitle>
          <ProductItemsContainer>
            {refillOptions.map((opt) => (
              <ProductButton
                key={opt.id}
                onClick={() => handleRefillChange(opt)}
                selected={opt.id === modalRefillId}
              >
                <img src={opt.image} />
                <ProductTitle>{opt.editModalProductName}</ProductTitle>
                <PriceContainer>
                  <span>${opt.discountedPrice}</span>
                  <del>${opt.fullPrice}</del>
                </PriceContainer>
              </ProductButton>
            ))}
          </ProductItemsContainer>
        </SectionContainer>
        <SectionContainer>
          <SectionTitle>HOW OFTEN</SectionTitle>
          <CadenceContainer>
            {cadenceOptions.map((opt) => {
              const isSelected = opt.value === modalCadence;
              return (
                <CadenceButton
                  key={opt.value}
                  selected={isSelected}
                  onClick={() => setModalCadence(opt.value)}
                >
                  <span>{opt.title}</span>
                  <span>{opt.subtitle}</span>
                  {opt.isMostPopular && (
                    <ButtonSticker>Most Popular</ButtonSticker>
                  )}
                  {isSelected && (
                    <ButtonCheckedIcon>
                      <CheckIcon />
                    </ButtonCheckedIcon>
                  )}
                </CadenceButton>
              );
            })}
          </CadenceContainer>
        </SectionContainer>
        <UpdateButton onClick={handleUpdate}>Update</UpdateButton>
      </Container>
    </StyledModal>
  );
};

export default EditRefillModal;
